import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { UserPlus, RotateCcw } from "lucide-react";

import { getAllStudents, getAllClasses } from "../api/student/studentApi";
import {
  getAllStudentsForPortal,
  getAllClassesForPortal,
} from "../api/student/studentPortalApi";

import StudentTable from "../components/StudentTable";
import { useStaffAuth } from "../context/StaffAuthContext";
import {
  Card,
  CardContent,
  Select,
  Button,
  Skeleton,
} from "../components/ui";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "ENROLLED", label: "Enrolled" },
  { value: "LEFT", label: "Left" },
  { value: "TRANSFERRED", label: "Transferred" },
  { value: "GRADUATED", label: "Graduated" },
];

const StudentList = ({ isPortal = false }) => {
  const { hasPermission } = useStaffAuth();

  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({ classId: "", status: "" });

  const loadStudents = useCallback(async () => {
    try {
      const activeFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== "")
      );

      const data = isPortal
        ? await getAllStudentsForPortal(activeFilters)
        : await getAllStudents(activeFilters);

      setStudents(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters, isPortal]);

  const loadClasses = useCallback(async () => {
    try {
      const data = isPortal
        ? await getAllClassesForPortal()
        : await getAllClasses();
      setClasses(data || []);
    } catch (err) {
      console.error(err);
    }
  }, [isPortal]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const canCreate = isPortal
    ? hasPermission("STUDENT_MANAGEMENT_FULL")
    : true;

  const classOptions = [
    { value: "", label: "All Classes" },
    ...classes.map((cls) => ({
      value: cls._id,
      label: `Class ${cls.className}`,
    })),
  ];

  const handleClearFilters = () => {
    setFilters({ classId: "", status: "" });
  };

  const createPath = isPortal
    ? "/staff-panel/students/create"
    : "/students/create";

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header – no decorative icon */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-xl sm:text-2xl font-bold tracking-tight text-neutral-900 dark:text-neutral-100">
            Student Management
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
            Manage student directory, view profiles, and update academic
            statuses.
          </p>
        </div>

        {canCreate && (
          <Link to={createPath}>
            <Button
              type="button"
              variant="primary"
              leftIcon={<UserPlus className="h-4 w-4" />}
              className="w-full sm:w-auto"
            >
              Add Student
            </Button>
          </Link>
        )}
      </div>

      {/* Filters Card */}
      <Card>
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-end justify-between">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1 max-w-2xl">
              <Select
                label="Class Filter"
                value={filters.classId}
                onChange={(val) =>
                  setFilters((prev) => ({ ...prev, classId: val }))
                }
                options={classOptions}
              />

              <Select
                label="Status Filter"
                value={filters.status}
                onChange={(val) =>
                  setFilters((prev) => ({ ...prev, status: val }))
                }
                options={STATUS_OPTIONS}
              />
            </div>

            <div className="flex items-center gap-2 pt-1 md:pt-0">
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={handleClearFilters}
                leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
                className="w-full sm:w-auto text-xs"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table Section */}
      {loading ? (
        <Card>
          <CardContent className="p-6 space-y-4">
            <Skeleton height={20} width="30%" />
            <Skeleton height={40} width="100%" />
            <Skeleton height={40} width="100%" />
            <Skeleton height={40} width="100%" />
          </CardContent>
        </Card>
      ) : (
        <StudentTable
          students={students}
          refreshStudents={loadStudents}
          isPortal={isPortal}
        />
      )}
    </div>
  );
};

export default StudentList;
